import { Button } from '@/components/ui/button'
import { CAPABILITIES, Capability } from './capabilities'
import { ProductTab, TAB_LABEL } from './prototypeNav'

/** Which capability sells each product tab. */
const SELLS: Record<ProductTab, Capability> = {
  [ProductTab.INSURANCE]: Capability.INSURANCE,
  [ProductTab.BOOKKEEPER]: Capability.BOOKKEEPING,
  [ProductTab.CFO]: Capability.CFO,
  [ProductTab.COMPLIANCE]: Capability.COMPLIANCE,
  [ProductTab.FUEL]: Capability.FUEL,
}

export interface BlankStateDetail {
  /** Leads the offer in place of the capability's own name. */
  heading?: string
  /** Stands in for whatever the product shows off here — a chart, a screen. */
  placeholder?: boolean
  /** The real thing, where the product has something to show rather than a box. */
  preview?: React.ReactNode
  /** Said under the product's name, before the price. */
  lines?: string[]
  /** Where to read more, in place of stating a price here. */
  learnMore?: string
  /** What that link says, where "Learn more" is not specific enough. */
  learnMoreLabel?: string
  /** Replaces the capability's own button, for an ask it cannot express. */
  action?: React.ReactNode
}

/** A product the fleet has not turned on: the tab is there, the product is an offer. */
export function ProductBlankState({
  tab,
  label,
  detail,
}: {
  tab: ProductTab
  label?: string
  detail?: BlankStateDetail
}) {
  const capability = CAPABILITIES[SELLS[tab]]

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      <h1 className="text-ink text-xl font-bold tracking-tight">{label ?? TAB_LABEL[tab]}</h1>
      <div className="border-ink-100 flex flex-1 flex-col items-center justify-center gap-3 rounded-md border border-dashed p-6">
        {detail?.preview ??
          (detail?.placeholder && <div className="bg-ink-50 h-56 w-full max-w-md rounded-md" aria-hidden />)}
        <div className="flex min-h-14 max-w-md flex-col items-center justify-start gap-1 text-center">
          <p className="text-ink text-sm font-semibold">{detail?.heading ?? capability.name}</p>
          {detail?.lines?.map((line) => (
            <p key={line} className="text-ink-500 text-xs leading-snug">
              {line}
            </p>
          ))}
        </div>
        <div className="flex h-10 items-center">
          {detail?.action ?? (
            <Button size="sm" className="text-xs">
              {capability.cta}
            </Button>
          )}
        </div>
        {detail?.learnMore ? (
          <Button asChild size="sm" variant="link" className="text-xs">
            <a href={detail.learnMore} target="_blank" rel="noopener noreferrer">
              {detail.learnMoreLabel ?? 'Learn more'}
            </a>
          </Button>
        ) : (
          capability.price && <p className="text-ink-500 text-xs">{capability.price}</p>
        )}
      </div>
    </div>
  )
}
